import {
    type Experiment,
    type ApiExperiment,
    convertApiExperimentToExperiment,
    convertExperimentToApiFormat,
} from 'src/api/voisino/experiments.model'
import { ins } from 'src/api/axios'
import { getHeaders } from 'src/api/headers'
import { getError, type BackendErrors } from 'src/api/utils'
import { type AxiosError } from 'axios'

// Fetch all experiments
export async function getExperiments(): Promise<Experiment[] | Error> {
    try {
        const timestamp = Date.now()
        const random = Math.random().toString(36).substring(7)
        const url = `/tests?_t=${timestamp}&_r=${random}`
        const headers = await getHeaders()

        const response = await ins.get(`/v1/voisino-api${url}`, { headers })

        const responseData = response.data
        const apiData: ApiExperiment[] = responseData

        // Convert API format to our internal format
        const convertedData = apiData.map(convertApiExperimentToExperiment)

        return convertedData
    } catch (error) {
        return getError(error as AxiosError<BackendErrors>, 'response.data.errors[0]')
    }
}

// Create a new experiment
export async function createExperiment(experiment: Experiment): Promise<Experiment | Error> {
    try {
        // Convert Experiment format to API format
        const apiExperiment = convertExperimentToApiFormat(experiment)

        // Filter out undefined values to avoid sending null/undefined to the API
        const filteredExperiment = Object.fromEntries(
            Object.entries(apiExperiment).filter(([_, value]) => value !== undefined),
        )
        const headers = await getHeaders()

        const response = await ins.post('/v1/voisino-api/test', filteredExperiment, { headers })

        const result = response.data

        // API returns full experiment data; convert it
        const createdExperiment = convertApiExperimentToExperiment(result)

        return createdExperiment
    } catch (error) {
        return getError(error as AxiosError<BackendErrors>, 'response.data.errors[0]')
    }
}

// Update test outcome
export async function updateTestOutcome(
    id: string,
    outcome: string,
    endDate?: string,
    lifecycleState?: string,
): Promise<void | Error> {
    try {
        const updateData: any = { outcome }

        if (endDate) {
            updateData.end_date = endDate
        }

        if (lifecycleState) {
            updateData.lifecycle_state = lifecycleState
        }

        const headers = await getHeaders()
        await ins.put(`/v1/voisino-api/test/${id}`, updateData, { headers })
        // success, no return value needed
        return
    } catch (error) {
        return getError(error as AxiosError<BackendErrors>, 'response.data.errors[0]')
    }
}
